import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react';

import EmployeeSiteBlock from './EmployeeSiteBlock';
import DrsAndAAASeal from '@/components/DrsAndAAASeal';
import EsgContainer from './EsgContainer';
import { AppContext, ContextType } from '@/shared/context';
import { checkSupression } from '@/utils/index';
import { properties } from '@/testData/companyProfile';
import getRevenueReliability from '../helper';

const CompanySnapshot = ({ props, countryCode, financialItems, esgData, print, buttonAction }) => {
    const { isUnitedStates } = useContext(AppContext) as ContextType;
    const { t } = useTranslation();

    return (
        <div
            className="col-md-5 offset-1  col-sm-offset-0 company-profile-overview-starting-margin"
            id="company_profile_snapshot"
        >
            {isUnitedStates && (
                <div className="row margin-between-rows company_snapshot_display" id="print_friendly_button">
                    <div className="col-md-1 company-profile-overview-icon-print-view"></div>
                    <div className="col-md-11">
                        <span
                            className="company_data_point company-profile-text"
                            name="company_print_view"
                            id="company-print-view"
                        >
                            <span className="company_profile_overview_underline_links">
                                <a
                                    className="company_profile_overview_print_view"
                                    href="#"
                                    onClick={(ev) => { print(ev) }}
                                >
                                    Printer Friendly View
                                </a>
                            </span>
                        </span>
                    </div>
                </div>
            )}
            <div className={`row mt-0 company_snapshot_display m-0 d-print-p-0 pl-20px ${props.drs ? 'margin-between-rows' : ''}`}>
                {props.drs && (
                    <DrsAndAAASeal
                        isHu={countryCode === "hu"}
                        meritColor={props.meritColor}
                        meritScore={props.meritScore}
                        drsContentLocalized={t("companyProfile.overview.sealText")}
                    />
                )}
            </div>

            <div className="row mt-0 margin-between-rows company_snapshot_display m-0 d-print-p-0">
                <div className="col-md-1 company-profile-overview-icon-address"></div>
                <div className="col-md-11">
                    <div className="company_data_point company-profile-text d-flex">
                        <label className="flex-shrink-0">
                            {t("companyProfile.overview.address_label")}:&nbsp;
                        </label>
                        <div>
                            <span className="company-profile-text">
                                <a
                                    data-sly-unwrap={
                                        props.partnershipCompany || props.proprietorshipCompany
                                    }
                                    href={`https://maps.google.com/?q=${props.companyLocation.replace('?', '')}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="ext-class ext-icon"
                                >
                                    {checkSupression(props.companyLocation)}
                                </a>
                                {props.isAddressUnverified && (
                                    <Tippy
                                        theme="dnbtooltip"
                                        content="Information needs to be verified"
                                        className="reliability-tool-tip"
                                    >
                                        <span className="icon unverified-address-icon"></span>
                                    </Tippy>
                                )}
                                {props.showSeeOtherLocations && (
                                    <span className="overview_arrow_link d-print-none">
                                        <a
                                            href={properties.seeOtherLocationsAnchor}
                                            className="jump-link cursor-pointer"
                                            onClick={(e) => { e.preventDefault(); document.getElementById("corporate-family")?.scrollIntoView({ behavior: "smooth" }) }}
                                        >
                                            {properties.seeOtherLocationLabel}
                                            <span className="chevron-right-arrow-blue"></span>
                                        </a>
                                    </span>
                                )}

                                {props.suppressContactData && (
                                    <div className="overview-location-subscription">
                                        {properties.subscriptionMessage}
                                    </div>
                                )}
                            </span>

                            {props.companyDisplayLocation && (
                                <>
                                    <br />
                                    <span className="avenir-light">
                                        {props.companyDisplayLocation}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {props.phone && (
                <div className="row mt-0 margin-between-rows company_snapshot_display m-0 d-print-p-0">
                    <div className="col-md-1 company-profile-overview-icon-phone"></div>
                    <div className="col-md-11">
                        <div className="company_data_point company-profile-text d-flex">
                            <label className="flex-shrink-0">
                                {t("companyProfile.overview.business_phone_label")}:&nbsp;
                            </label>
                            <span
                                data-tracking-name={t(
                                    "companyProfile.overview.business_phone_label"
                                )}
                            >
                                {checkSupression(props.phone)}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            {props.website && (
                <div className="row margin-between-rows company_snapshot_display">
                    <div className="col-md-1 company-profile-overview-icon-website"></div>
                    <div className="col-md-11">
                        <div className="company_data_point company-profile-text d-flex">
                            <label className="flex-shrink-0">
                                {t("companyProfile.overview.business_website_label")}
                                :&nbsp;
                            </label>
                            <span className="company_profile_overview_underline_links">
                                <a
                                    href={props.website?.startsWith('http') ? props.website : `http://${props.website}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    id="hero-company-link"
                                    className="ext-class ext-icon website"
                                >
                                    {props.website}
                                </a>
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {props.employeesThisSiteValue ||
                props.employeesConsolidatedValue ? (
                <EmployeeSiteBlock
                    isEuBd={properties.isEuBd}
                    employeesConsolidatedValue={props.employeesConsolidatedValue}
                    employeesThisSiteValue={props.employeesThisSiteValue}
                    employeesThisSiteReliability={
                        props.employeesThisSiteReliability
                    }
                    employeesConsolidatedReliability={
                        props.employeesConsolidatedReliability
                    }
                    countryCode={countryCode}
                />
            ) : (
                <></>
            )}

            {props.showRevenueRow && isUnitedStates && (
                <div className="row margin-between-rows company_snapshot_display">
                    <div className="col-md-1 company-profile-overview-icon-revenue"></div>
                    <div className="col-md-11 flex-shrink-1 d-flex-none d-md-flex flex-wrap align-items-center">
                        {financialItems.map((item, index) => (
                            <React.Fragment key={item.key}>
                                {item}
                                {index < financialItems.length - 1 && <div className="d-none d-md-block d-lg-block vr opacity-100 mx-2 my-1 text-muted" />}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}

            {properties.isEuBd && (
                <>
                    {props.revenue && (
                        <div className="row mt-0 margin-between-rows company_snapshot_display m-0 d-print-p-0 ">
                            <div className="col-md-1 company-profile-overview-icon-revenue"></div>
                            <div className="col-md-11">
                                <span
                                    className="company_data_point company-profile-text"
                                    name="revenue_in_us_dollar"
                                >
                                    <label>
                                        {t("companyProfile.overview.revenue_label")}:&nbsp;
                                    </label>
                                    <span>{checkSupression(props.revenue, { buttonLabel: t("companyProfile.overview.revenueBtnLabel"), text: t("companyProfile.overview.revenuePopupDescription"), type: 'revenue', buttonAction: buttonAction })}</span>
                                    {getRevenueReliability(
                                        props.revenueReliability,
                                        t,
                                        countryCode
                                    )}
                                </span>
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className="row margin-between-rows company_snapshot_display">
                <div className="col-md-1 company-profile-overview-icon-office-building"></div>
                <div className="col-md-11 d-flex align-items-center flex-wrap">
                    <div className="company_data_point company-profile-text d-flex">
                        {props.yearStarted && (
                            <>
                                <label className="flex-shrink-0">
                                    {t("companyProfile.overview.year_started_label")}:&nbsp;
                                </label>
                                <span
                                    data-tracking-name={t(
                                        "companyProfile.overview.year_started_label"
                                    )}
                                >
                                    {checkSupression(props.yearStarted)}
                                </span>
                            </>
                        )}
                    </div>

                    {isUnitedStates && props.incorporatedYear && (
                        <div className="company_data_point company-profile-text d-flex">
                            <label className="flex-shrink-0">
                                {t("companyProfile.overview.incorporated_label")}:&nbsp;
                            </label>
                            <span
                                data-tracking-name={t(
                                    "companyProfile.overview.incorporated_label"
                                )}
                            >
                                {checkSupression(props.incorporatedYear)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {isUnitedStates && props.stockExchange && (
                <div className="row margin-between-rows company_snapshot_display">
                    <div className="col-md-1 company-profile-overview-icon-stockExchange"></div>
                    <div className="col-md-11">
                        <div className="company_data_point company-profile-text d-flex">
                            <label className="flex-shrink-0">{properties.stock_exchange_label}:&nbsp;</label>
                            <span className="text-truncate">{props.stockExchange}</span>
                        </div>
                    </div>
                </div>
            )}

            {isUnitedStates && <EsgContainer esgData={esgData} />}

            <div className="company-profile-divider"></div>

            <div className="row margin-between-rows company_snapshot_display">
                <div className="col-md-12">
                    <span className="company_data_point company-profile-text">
                        <a
                            className="overview_button_cta button  overview_cta_btn overview_button_cta"
                            href={t("companyProfile.overview.staticFreeTrialLink")}
                            target="_self"
                        >
                            {t("companyProfile.overview.staticFreeTrialText")}
                        </a>
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CompanySnapshot;